/**
 * OnCallSchedule.tsx — Weekly on-call schedule grid
 *
 * Purpose:    Renders a 7-day x 24-hour grid showing on-call coverage slots.
 *             Shaded cells indicate covered hours; hover shows assignee name.
 * Inputs:     slots: Array<{ day, startHour, endHour, assignee }>
 * Outputs:    Responsive table with shaded cells per slot
 * Constraints: No external calendar dependency; pure display component
 */

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export function OnCallSchedule({
  slots,
}: {
  slots: Array<{ day: number; startHour: number; endHour: number; assignee: string }>
}) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs">
        <thead>
          <tr>
            <th className="w-20 py-2 text-left text-brand-muted">Day</th>
            {Array.from({ length: 24 }, (_, h) => (
              <th
                key={h}
                className="px-1 text-center text-brand-muted"
                style={{ minWidth: 28 }}
              >
                {h % 6 === 0 ? `${h}h` : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {DAYS.map((day, dayIdx) => {
            const daySlots = slots.filter((s) => s.day === dayIdx)
            return (
              <tr key={day} className="border-t border-brand-subtle">
                <td className="py-1.5 font-medium text-brand-muted">{day}</td>
                {Array.from({ length: 24 }, (_, hour) => {
                  const slot = daySlots.find(
                    (s) => hour >= s.startHour && hour < s.endHour,
                  )
                  return (
                    <td
                      key={hour}
                      className={`h-7 rounded-sm ${slot ? 'bg-sky-800/60' : ''}`}
                      title={slot ? slot.assignee : undefined}
                    />
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
      <p className="mt-1 text-xs text-brand-muted">
        Shaded cells indicate on-call coverage. Hover for assignee.
      </p>
    </div>
  )
}
